/* @flow */
import PropTypes from 'prop-types';
import * as React from 'react';
import Modal from 'react-modal';

import CurrentUser from 'auth/CurrentUser';
import ActionsList from 'components/ActionsList';
import ConfirmDialog from 'components/ConfirmDialog';
import Container from 'components/Container';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import Link from 'components/Link';
import ModalTrigger from 'components/ModalTrigger';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Text from 'components/Text';
import WizardLayout from 'components/WizardLayout';
import Group from 'entities/Group';
import UsersInviteLegalDialog from 'groups/components/UsersInviteLegalDialog';
import GroupAvatarWithText from 'groups/components/GroupAvatarWithText';
import UsersInvite from 'users/components/UsersInviteModal/UsersInvite';
import UsersPdfInviteInstructions from 'users/components/UsersInviteModal/UsersPdfInviteInstructions';
import UsersShareJoinUrl from 'users/components/UsersInviteModal/UsersShareJoinUrl';
import UsersSmsInviteInstructions from 'users/components/UsersInviteModal/UsersSmsInviteInstructions';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reinitialize } from 'reducers/directAddForm';

const EVENT_SCREEN_NAME = 'UsersInviteModal';

type Props = {
  group: Group,
  onRequestHide: () => any,
  reinitialize: () => void,
  directAddContainsData: boolean,
};

type State = {
  activeAction: string,
  isConfirmationDialogOpened: boolean,
};

class UsersInviteModal extends React.Component<Props, State> {
  static defaultProps = {
    onRequestHide: () => {},
  };

  static childContextTypes = {
    eventScreenName: PropTypes.string,
  };

  state = {
    activeAction: 'direct-add',
    isConfirmationDialogOpened: false,
  };

  getChildContext() {
    return {
      eventScreenName: EVENT_SCREEN_NAME,
    };
  }

  handleHideRequest = () => {
    const { onRequestHide, directAddContainsData } = this.props;

    if (directAddContainsData) {
      this.setState({ isConfirmationDialogOpened: true });
    } else if (onRequestHide) {
      this.clearStateAndClose();
    }
  };

  clearStateAndClose = () => {
    const { onRequestHide } = this.props;

    // clear redux state
    this.props.reinitialize();
    onRequestHide();
  };

  setActiveAction = (action: string) => {
    this.setState({ activeAction: action });
  };

  smsEnabled(): boolean {
    return this.props.group.smsEnabledForUser(CurrentUser.getUser());
  }

  render() {
    const { group, onRequestHide } = this.props;
    const { activeAction, isConfirmationDialogOpened } = this.state;

    if (!activeAction) {
      return null;
    }

    const smsEnabled = this.smsEnabled();

    return (
      <React.Fragment>
        <Modal isOpen={isConfirmationDialogOpened}>
          <ConfirmDialog
            actionButtonText="Go back"
            cancelButtonText="Leave"
            hideCloseButton
            hideOnBlur={false}
            title="Leave without adding people?"
            subtext="These contacts won’t be saved. If you leave this page, you’ll need to enter this information again."
            onConfirm={() => this.setState({ isConfirmationDialogOpened: false })}
            onCancel={this.clearStateAndClose}
            onRequestHide={onRequestHide}
            eventScreenName="UsersInviteModal"
          />
        </Modal>
        <Dialog
          full
          tabActive={this.state.activeAction}
          className="UsersInviteModal"
          onDialogDismiss={this.handleHideRequest}
          onRequestHide={onRequestHide}
          eventScreenName="UsersInviteModal">
          <WizardLayout
            headerIcon="avatar-add-square"
            headerIconColor="shamrock"
            title="Add People"
            onBack={this.handleHideRequest}
            sidebar={
              <React.Fragment>
                <Text align="left" element="div">
                  <GroupAvatarWithText group={group} />
                  <Row size="xlarge">
                    <Divider size="xxxlarge" />
                  </Row>
                  <ActionsList
                    data={[
                      {
                        items: [
                          {
                            action: () => this.setActiveAction('direct-add'),
                            active: activeAction === 'direct-add',
                            copy: 'Phone or email contacts',
                            icon: 'contact-card',
                            iconColor: 'shamrock',
                          },
                          {
                            action: () => this.setActiveAction('pdf-instructions'),
                            active: activeAction === 'pdf-instructions',
                            copy: 'Printable PDFs',
                            icon: 'printer',
                            iconColor: 'lava',
                          },
                          {
                            action: () => this.setActiveAction('sms-instructions'),
                            active: activeAction === 'sms-instructions',
                            copy: 'In-person instructions',
                            hide: !smsEnabled,
                            icon: 'display-board-stroked',
                            iconColor: 'violet',
                          },
                          {
                            action: () => this.setActiveAction('share-join-url'),
                            active: activeAction === 'share-join-url',
                            copy: 'Share a link',
                            icon: 'link',
                            iconColor: 'flame',
                          },
                        ],
                      },
                    ]}
                  />
                </Text>
                <Notice border color="polar-bear" noShadow>
                  <Text size="xxsmall">
                    Make sure you have permission to message the people you add.{' '}
                    <ModalTrigger
                      modal={
                        <UsersInviteLegalDialog hasChildren={this.props.group.hasChildren()} />
                      }>
                      <Link>Learn more</Link>
                    </ModalTrigger>
                  </Text>
                </Notice>
              </React.Fragment>
            }>
            <Container flush>
              {activeAction === 'direct-add' && (
                <UsersInvite
                  group={group}
                  supportSms={smsEnabled}
                  onClose={this.clearStateAndClose}
                />
              )}
              {activeAction === 'pdf-instructions' && <UsersPdfInviteInstructions group={group} />}
              {activeAction === 'sms-instructions' &&
                smsEnabled && <UsersSmsInviteInstructions group={group} />}
              {activeAction === 'share-join-url' && <UsersShareJoinUrl group={group} />}
            </Container>
          </WizardLayout>
        </Dialog>
      </React.Fragment>
    );
  }
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ reinitialize }, dispatch);
}

function mapStateToProps(state) {
  return {
    directAddContainsData: state.directAddForm.containsData,
  };
}

export { UsersInviteModal };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UsersInviteModal);
